/*********************************************************************
 * © Copyright IBM Corp. 2022
 * Copyright © 2022 Randori https://randori.com - All Rights Reserved.
 *********************************************************************/
import { LogLevel } from '@grafana/faro-web-sdk'
import { isNotNil } from '@randori/rootkit'
import debug from 'debug'
import { isError } from 'lodash/fp'

import { getUserType } from './userContext'

// ---------------------------------------------------------------------------

const _log = debug('RANDORI:logger')
const _logNoop = debug('RANDORI:logger noop')

// eslint-disable-next-line lodash-fp/prefer-constant
function _getIsTracked(): boolean {
  return true
}

const _getFaro = () => {
  if (isNotNil(window?.__RUTIL__?.tracer?.getTracer)) {
    return window.__RUTIL__.tracer.getTracer()
  }
}

type LoggerConfig = {
  getFaro: typeof _getFaro
  getIsTracked: typeof _getIsTracked
  log: typeof _log
  logNoop: typeof _logNoop
}

// ---------------------------------------------------------------------------

// TODO - make updates where necessary to pass in options
function _exception({ getFaro, getIsTracked, log, logNoop }: LoggerConfig) {
  return function (
    err: unknown,
    options?: {
      status?: number
      url?: string
      method?: string
      host?: string
      level?: LogLevel
    },
  ) {
    const faro = getFaro()
    const isTracked = getIsTracked()
    const userType = getUserType()

    let httpInfo: Record<string, any> = {}
    const hasHttpInfo = isNotNil(options?.status) || isNotNil(options?.url) || isNotNil(options?.host)

    if (hasHttpInfo) {
      httpInfo = {
        http_host: options.host ?? (isNotNil(options.url) ? new URL(options.url).host : undefined),
        http_request_url: options.url,
        http_method: options.method ?? 'GET',
        http_status_code: options.status,
      }
    }

    if (isNotNil(faro) && isTracked && isError(err)) {
      faro.api.pushError(err, {
        context: {
          userType,
          ...httpInfo,
        },
      })

      const exceptionMsg = `kind=exception error=${err.name} message=${err.message} userType=${userType}`
      log(exceptionMsg)
    } else {
      const noopMsg = `kind=exception message=${err} userType=${userType}`
      logNoop(noopMsg)
    }
  }
}

function _message({ getFaro, getIsTracked, log, logNoop }: LoggerConfig) {
  return function (
    msg: string,
    options?: {
      status?: number
      url?: string
      method?: string
      host?: string
      level?: LogLevel
    },
  ) {
    const faro = getFaro()
    const isTracked = getIsTracked()
    const userType = getUserType()

    let httpInfo: Record<string, any> = {}

    const stringMsg = typeof msg === 'string' ? msg : JSON.stringify(msg)
    const hasHttpInfo = isNotNil(options?.status) || isNotNil(options?.url) || isNotNil(options?.host)

    if (hasHttpInfo) {
      httpInfo = {
        http_host: options.host ?? (isNotNil(options.url) ? new URL(options.url).host : undefined),
        http_request_url: options.url,
        http_method: options.method ?? 'GET',
        http_status_code: options.status,
      }
    }

    const logMessage = `message=${stringMsg} userType=${userType}`

    if (isNotNil(faro) && isTracked) {
      // send structured log to faro
      faro.api.pushLog([stringMsg], {
        level: options?.level ?? LogLevel.INFO,
        context: {
          userType,
          ...httpInfo,
        },
      })

      log('message', logMessage)
    } else {
      logNoop('message', logMessage)
    }
  }
}

function _setUser({ getFaro, getIsTracked, log, logNoop }: LoggerConfig) {
  type User = {
    id: string
    org_id: string
  }

  const setUser = (user: User) => {
    const faro = getFaro()
    const isTracked = getIsTracked()

    if (isNotNil(faro) && isTracked) {
      faro.api.setUser(user)

      log('Exception tracking context set')
    } else {
      logNoop('Exception tracking context set')
    }
  }

  return setUser
}

function _unsetUser({ getFaro, getIsTracked, log, logNoop }: LoggerConfig) {
  return () => {
    const faro = getFaro()
    const isTracked = getIsTracked()

    if (isNotNil(faro) && isTracked) {
      faro.api.resetUser()

      log('Exception tracking context cleared')
    } else {
      logNoop('Exception tracking context cleared')
    }
  }
}

// ---------------------------------------------------------------------------

export const error = _exception({ getFaro: _getFaro, log: _log, logNoop: _logNoop, getIsTracked: _getIsTracked })
export const log = _message({ getFaro: _getFaro, log: _log, logNoop: _logNoop, getIsTracked: _getIsTracked })
export const setUser = _setUser({ getFaro: _getFaro, log: _log, logNoop: _logNoop, getIsTracked: _getIsTracked })
export const unsetUser = _unsetUser({ getFaro: _getFaro, log: _log, logNoop: _logNoop, getIsTracked: _getIsTracked })

export const Test = {
  _exception,
  _getIsTracked,
  _message,
  _setUser,
  _unsetUser,
}
